import { safeLocalStorage } from "@/utils/safeStorage";
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { useIsMobile } from '@/hooks/use-mobile';

// Theme types
export type V2Theme = 'neon' | 'noir' | 'solar';
export type FxPreference = 'auto' | 'full' | 'low';
export type ResolvedFx = 'full' | 'low';

// Granular effect controls
export interface GranularFxSettings {
  enableBlur: boolean;
  enableAurora: boolean;
  enableGrid: boolean;
  enableGlow: boolean;
  enableAnimations: boolean;
  enableParticles: boolean;
  enableCursorEffects: boolean;
}

interface UIV2State {
  uiV2Enabled: boolean;
  theme: V2Theme;
  fxPref: FxPreference;
  resolvedFx: ResolvedFx;
  surpriseMode: boolean;
  partyMode: boolean;
  // Enhanced performance features
  autoOptimize: boolean; // Auto-reduce effects when performance drops
  granularFx: GranularFxSettings;
  performanceScore: number; // 0-100, updated by performance monitor
}

const DEFAULT_GRANULAR_FX: GranularFxSettings = {
  enableBlur: true,
  enableAurora: true,
  enableGrid: true,
  enableGlow: true,
  enableAnimations: true,
  enableParticles: true,
  enableCursorEffects: true,
};

const LOW_FX_GRANULAR: GranularFxSettings = {
  enableBlur: false,
  enableAurora: false,
  enableGrid: false,
  enableGlow: false,
  enableAnimations: false,
  enableParticles: false,
  enableCursorEffects: false,
};

interface UIV2ContextType extends UIV2State {
  setUIV2Enabled: (enabled: boolean) => void;
  setTheme: (theme: V2Theme) => void;
  setFxPref: (pref: FxPreference) => void;
  setSurpriseMode: (enabled: boolean) => void;
  triggerPartyMode: () => void;
  // Enhanced performance features
  setAutoOptimize: (enabled: boolean) => void;
  setGranularFx: (settings: Partial<GranularFxSettings>) => void;
  updatePerformanceScore: (score: number) => void;
  isLowFx: boolean;
  effectiveGranularFx: GranularFxSettings; // Computed based on resolvedFx
}

const STORAGE_KEY = 'geniebot-ui-v2-settings';

const DEFAULT_STATE: UIV2State = {
  uiV2Enabled: true,
  theme: 'neon',
  fxPref: 'auto',
  resolvedFx: 'full',
  surpriseMode: true,
  partyMode: false,
  autoOptimize: true, // Enabled by default
  granularFx: DEFAULT_GRANULAR_FX,
  performanceScore: 100,
};

const VALID_THEMES: V2Theme[] = ['neon', 'noir', 'solar'];
const VALID_FX_PREFS: FxPreference[] = ['auto', 'full', 'low'];

const UIV2Context = createContext<UIV2ContextType | undefined>(undefined);

function getStoredSettings(): Partial<UIV2State> {
  if (typeof window === 'undefined') return {};
  
  try {
    const stored = safeLocalStorage.getItem(STORAGE_KEY);
    if (!stored) return {};
    
    const parsed = JSON.parse(stored);
    
    // Validate stored values
    const validated: Partial<UIV2State> = {};
    
    if (typeof parsed.uiV2Enabled === 'boolean') {
      validated.uiV2Enabled = parsed.uiV2Enabled;
    }
    
    if (VALID_THEMES.includes(parsed.theme)) {
      validated.theme = parsed.theme;
    }
    
    if (VALID_FX_PREFS.includes(parsed.fxPref)) {
      validated.fxPref = parsed.fxPref;
    }
    
    if (typeof parsed.surpriseMode === 'boolean') {
      validated.surpriseMode = parsed.surpriseMode;
    }
    
    if (typeof parsed.autoOptimize === 'boolean') {
      validated.autoOptimize = parsed.autoOptimize;
    }
    
    if (parsed.granularFx && typeof parsed.granularFx === 'object') {
      validated.granularFx = { ...DEFAULT_GRANULAR_FX, ...parsed.granularFx };
    }
    
    return validated;
  } catch {
    return {};
  }
}

function resolveEffectsMode(
  fxPref: FxPreference,
  isMobile: boolean,
  prefersReducedMotion: boolean,
  autoOptimize: boolean,
  performanceScore: number
): ResolvedFx {
  if (fxPref === 'full') return 'full';
  if (fxPref === 'low') return 'low';
  
  // Auto mode: detect conditions
  if (prefersReducedMotion) return 'low';
  if (isMobile) return 'low';
  if (typeof navigator !== 'undefined' && navigator.maxTouchPoints > 0) return 'low';
  
  // Auto-optimize based on performance score
  if (autoOptimize && performanceScore < 40) return 'low';
  
  return 'full';
}

export function UIV2Provider({ children }: { children: ReactNode }) {
  const isMobile = useIsMobile();
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  
  const [state, setState] = useState<UIV2State>(() => ({
    ...DEFAULT_STATE,
    ...getStoredSettings(),
  }));
  
  // Check prefers-reduced-motion
  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    setPrefersReducedMotion(mediaQuery.matches);
    
    const handler = (e: MediaQueryListEvent) => setPrefersReducedMotion(e.matches);
    mediaQuery.addEventListener('change', handler);
    return () => mediaQuery.removeEventListener('change', handler);
  }, []);
  
  // Resolve FX mode when dependencies change
  useEffect(() => {
    const resolved = resolveEffectsMode(
      state.fxPref, 
      isMobile, 
      prefersReducedMotion, 
      state.autoOptimize, 
      state.performanceScore
    );
    if (resolved !== state.resolvedFx) {
      setState(prev => ({ ...prev, resolvedFx: resolved }));
    }
  }, [state.fxPref, isMobile, prefersReducedMotion, state.resolvedFx, state.autoOptimize, state.performanceScore]);
  
  // Apply data attributes to document
  useEffect(() => {
    const root = document.documentElement;
    
    root.dataset.v2Enabled = String(state.uiV2Enabled);
    root.dataset.v2Theme = state.theme;
    root.dataset.fx = state.resolvedFx;
    root.dataset.partyMode = String(state.partyMode);
    
    // Cleanup on unmount
    return () => {
      delete root.dataset.v2Enabled;
      delete root.dataset.v2Theme;
      delete root.dataset.fx;
      delete root.dataset.partyMode;
    };
  }, [state.uiV2Enabled, state.theme, state.resolvedFx, state.partyMode]);
  
  // Persist settings
  useEffect(() => {
    try {
      safeLocalStorage.setItem(STORAGE_KEY, JSON.stringify({
        uiV2Enabled: state.uiV2Enabled,
        theme: state.theme,
        fxPref: state.fxPref,
        surpriseMode: state.surpriseMode,
        autoOptimize: state.autoOptimize,
        granularFx: state.granularFx,
      }));
    } catch (e) {
      console.warn('Failed to persist UI V2 settings:', e);
    }
  }, [state.uiV2Enabled, state.theme, state.fxPref, state.surpriseMode, state.autoOptimize, state.granularFx]);
  
  const setUIV2Enabled = useCallback((enabled: boolean) => {
    setState(prev => ({ ...prev, uiV2Enabled: enabled }));
  }, []);
  
  const setTheme = useCallback((theme: V2Theme) => {
    if (VALID_THEMES.includes(theme)) {
      setState(prev => ({ ...prev, theme }));
    }
  }, []);
  
  const setFxPref = useCallback((pref: FxPreference) => {
    if (VALID_FX_PREFS.includes(pref)) {
      setState(prev => ({ ...prev, fxPref: pref }));
    }
  }, []);
  
  const setSurpriseMode = useCallback((enabled: boolean) => {
    setState(prev => ({ ...prev, surpriseMode: enabled }));
  }, []);
  
  const setAutoOptimize = useCallback((enabled: boolean) => {
    setState(prev => ({ ...prev, autoOptimize: enabled }));
  }, []);
  
  const setGranularFx = useCallback((settings: Partial<GranularFxSettings>) => {
    setState(prev => ({
      ...prev,
      granularFx: { ...prev.granularFx, ...settings },
    }));
  }, []);
  
  const updatePerformanceScore = useCallback((score: number) => {
    setState(prev => {
      if (Math.abs(prev.performanceScore - score) < 5) return prev; // Debounce small changes
      return { ...prev, performanceScore: score };
    });
  }, []);
  
  const triggerPartyMode = useCallback(() => {
    if (state.resolvedFx === 'low' || prefersReducedMotion || !state.surpriseMode) {
      return; // Don't trigger party mode in low FX or reduced motion
    }
    
    setState(prev => ({ ...prev, partyMode: true }));
    
    // Auto-disable after 15 seconds
    setTimeout(() => {
      setState(prev => ({ ...prev, partyMode: false }));
    }, 15000);
  }, [state.resolvedFx, prefersReducedMotion, state.surpriseMode]);
  
  // Compute effective granular settings based on resolved FX mode
  const effectiveGranularFx: GranularFxSettings = state.resolvedFx === 'low'
    ? LOW_FX_GRANULAR
    : state.granularFx;
  
  const value: UIV2ContextType = {
    ...state,
    setUIV2Enabled,
    setTheme,
    setFxPref,
    setSurpriseMode,
    triggerPartyMode,
    setAutoOptimize,
    setGranularFx,
    updatePerformanceScore,
    isLowFx: state.resolvedFx === 'low',
    effectiveGranularFx,
  };
  
  return (
    <UIV2Context.Provider value={value}>
      {children}
    </UIV2Context.Provider>
  );
}

export function useUIV2() {
  const context = useContext(UIV2Context);
  if (context === undefined) {
    throw new Error('useUIV2 must be used within a UIV2Provider');
  }
  return context;
}

// Safe hook that returns defaults when outside provider
export function useUIV2Safe(): UIV2ContextType {
  const context = useContext(UIV2Context);
  if (context === undefined) {
    return {
      ...DEFAULT_STATE,
      setUIV2Enabled: () => {},
      setTheme: () => {},
      setFxPref: () => {},
      setSurpriseMode: () => {},
      triggerPartyMode: () => {},
      setAutoOptimize: () => {},
      setGranularFx: () => {},
      updatePerformanceScore: () => {},
      isLowFx: false,
      effectiveGranularFx: DEFAULT_GRANULAR_FX,
    };
  }
  return context;
}
